import api from './api';
import type { MySubscription } from '../types/subscription';
import { queryCache, queryKeys, type FetchQueryOptions } from './queryCache';

export interface PlanOption {
  id: string;
  name: string;
  description: string;
  price_id: string;
  price: number;
  per_month: number;
  currency?: string;
  currency_symbol?: string;
  popular?: boolean;
}

export const subscriptionsApi = {
  getMySubscription: async (
    options: FetchQueryOptions = {},
  ): Promise<{ success: boolean; data: MySubscription | null }> =>
    queryCache.fetchQuery(
      queryKeys.subscription,
      async () => {
        const response = await api.get('/subscriptions/me');
        return response.data;
      },
      { staleTimeMs: 30_000, ...options },
    ),

  getPlans: async (
    platform?: 'ios' | 'android' | 'web',
    options: FetchQueryOptions = {},
  ): Promise<{ success: boolean; data: { plans: PlanOption[] } }> =>
    queryCache.fetchQuery(
      queryKeys.subscriptionPlans(platform),
      async () => {
        const response = await api.get('/subscriptions/plans', {
          params: platform ? { platform } : undefined,
        });
        return response.data;
      },
      { staleTimeMs: 10 * 60 * 1000, ...options },
    ),

  createCheckout: async (
    planId: string,
    options?: { successUrl?: string; cancelUrl?: string }
  ): Promise<{ checkout_url: string; session_id: string }> => {
    const response = await api.post<{ data: { checkout_url: string; session_id: string } }>(
      '/subscriptions/checkout',
      {
        plan_id: planId,
        success_url: options?.successUrl,
        cancel_url: options?.cancelUrl,
      }
    );
    return response.data.data;
  },

  syncCheckoutStatus: async (sessionId: string): Promise<{ plan_slug: string; status: string; current_period_end: string | null }> => {
    const response = await api.get<{ data: { plan_slug: string; status: string; current_period_end: string | null } }>(
      '/subscriptions/checkout-status',
      { params: { session_id: sessionId } }
    );
    queryCache.invalidateQueries(queryKeys.subscription);
    return response.data.data;
  },

  cancelSubscription: async (): Promise<{
    message: string;
    current_period_end: string | null;
    status?: string;
    can_cancel?: boolean;
    cancel_at_period_end?: boolean;
  }> => {
    const response = await api.post<{ data: {
      message: string;
      current_period_end: string | null;
      status?: string;
      can_cancel?: boolean;
      cancel_at_period_end?: boolean;
    } }>(
      '/subscriptions/cancel'
    );
    queryCache.invalidateQueries(queryKeys.subscription);
    return response.data.data;
  },
};
